import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  Check,
  Copy,
  Loader2,
  MessageSquarePlus,
  Plus,
  RefreshCw,
  Terminal,
  ToggleLeft,
  ToggleRight,
  Webhook,
  X,
} from 'lucide-react'
import {
  webhookService,
} from '../services/api'
import {
  API_BASE_URL,
} from '../config'
import {
  IntegrationRequestModal,
} from './IntegrationCategoriesSection'
import type {
  WebhookIntegrationResponse,
  WebhookIntegrationWithTokenResponse,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  copyTextToClipboard,
} from '../utils/clipboard'

function formatWebhookUrl(integration: WebhookIntegrationResponse): string {
  return `${API_BASE_URL}/api/v1/webhooks/incoming/${integration.id}`
}

type CopiedItemType = 'url' | 'token' | 'integrationId' | 'curl'

// ─── Create Webhook Modal ────────────────────────────────────────────

function CreateWebhookModal({
  onClose,
  onCreated,
  initialName = '',
  initialDescription = '',
}: {
  onClose: () => void
  onCreated: (created: WebhookIntegrationWithTokenResponse) => void
  initialName?: string | undefined
  initialDescription?: string | undefined
}) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setCreating(true)
    try {
      const created = await webhookService.create({
        name: name.trim(),
        description: description.trim() || null,
      })
      trackEvent(OnboardingEvents.WEBHOOK_CREATED)
      onCreated(created)
    } catch (error) {
      console.error('Failed to create webhook integration:', error)
    } finally {
      setCreating(false)
    }
  }

  const handleClose = () => {
    trackEvent(OnboardingEvents.WEBHOOK_CREATE_CLOSED)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Create Webhook</h2>
            <p className="text-sm text-gray-500 mt-0.5">Events sent to this webhook will be handled by your assistant</p>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CRM Lead Notifications"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
              maxLength={255}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What will this webhook be used for?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none text-sm"
              rows={3}
            />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={handleClose} disabled={creating} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={() => void handleCreate()} disabled={creating || !name.trim()} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Webhook Detail Modal ────────────────────────────────────────────

function WebhookDetailModal({
  integration,
  revealedToken,
  onClose,
  onToggleActive,
  onRegenerateToken,
  onDelete,
}: {
  integration: WebhookIntegrationResponse
  revealedToken: string | null
  onClose: () => void
  onToggleActive: () => void
  onRegenerateToken: () => void
  onDelete: () => void
}) {
  const [copied, setCopied] = useState<CopiedItemType | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)

  const handleCopy = useCallback(async (text: string, type: CopiedItemType) => {
    await copyTextToClipboard(text)
    setCopied(type)
    trackEvent(OnboardingEvents.WEBHOOK_VALUE_COPIED, { type })
    setTimeout(() => setCopied(null), 2000)
  }, [])

  const handleClose = () => {
    trackEvent(OnboardingEvents.WEBHOOK_MODAL_CLOSED)
    onClose()
  }

  const url = formatWebhookUrl(integration)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Webhook className="w-5 h-5 text-primary-500" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">{integration.name}</h2>
              {integration.description && <p className="text-sm text-gray-500">{integration.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_TOGGLED, { active: !integration.is_active }); onToggleActive() }} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors" title={integration.is_active ? 'Deactivate' : 'Activate'}>
              {integration.is_active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5" />}
            </button>
            <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {!integration.is_active && (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">This webhook is inactive</div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-50 px-3 py-2 rounded-lg font-mono text-gray-700 break-all">{url}</code>
              <button onClick={() => void handleCopy(url, 'url')} className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors">
                {copied === 'url' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Integration ID</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-50 px-3 py-2 rounded-lg font-mono text-gray-700">{integration.id}</code>
              <button onClick={() => void handleCopy(integration.id, 'integrationId')} className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors">
                {copied === 'integrationId' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {revealedToken && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <label className="block text-xs font-medium text-green-700 mb-1">Token</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-green-100 px-3 py-2 rounded font-mono text-green-800 break-all">{revealedToken}</code>
                <button onClick={() => void handleCopy(revealedToken, 'token')} className="flex-shrink-0 p-2 text-green-600 hover:text-green-800 transition-colors">
                  {copied === 'token' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-green-600 mt-2">Copy this token now. It won't be shown again.</p>
              <button
                onClick={() => {
                  const samplePayload = JSON.stringify({ summary: 'Test event', details: 'Hello from curl!' })
                  const curl = `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-Webhook-Token: ${revealedToken}' \\\n  -d '${samplePayload}'`
                  void handleCopy(curl, 'curl')
                }}
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors"
              >
                {copied === 'curl' ? <Check className="w-3.5 h-3.5" /> : <Terminal className="w-3.5 h-3.5" />}
                {copied === 'curl' ? 'Copied!' : 'Copy sample cURL'}
              </button>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_DELETE_CLICKED); setConfirmDelete(true) }} className="text-sm text-red-600 hover:text-red-700 font-medium">Delete</button>
          <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_REGENERATE_CLICKED); setConfirmRegenerate(true) }} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium">
            <RefreshCw className="w-3.5 h-3.5" /> Regenerate token
          </button>
        </div>

        {confirmDelete && (
          <div className="p-4 border-t border-red-100 bg-red-50">
            <p className="text-sm text-red-800 font-medium mb-2">Delete this webhook?</p>
            <p className="text-xs text-red-700 mb-3">This action cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_DELETED); onDelete() }} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Delete</button>
              <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_DELETE_CANCELLED); setConfirmDelete(false) }} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        )}
        {confirmRegenerate && (
          <div className="p-4 border-t border-yellow-100 bg-yellow-50">
            <p className="text-sm text-yellow-800 font-medium mb-2">Regenerate token?</p>
            <p className="text-xs text-yellow-700 mb-3">The current token will stop working.</p>
            <div className="flex gap-2">
              <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_TOKEN_REGENERATED); onRegenerateToken(); setConfirmRegenerate(false) }} className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700">Regenerate</button>
              <button onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_REGENERATE_CANCELLED); setConfirmRegenerate(false) }} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Category Card (matches IntegrationCategoriesSection style) ──────

export function OtherIntegrationsCard({
  pendingWebhook,
}: {
  pendingWebhook?: { name: string, description: string } | null | undefined
} = {}) {
  const [integrations, setIntegrations] = useState<WebhookIntegrationResponse[]>([])
  const [loading, setLoading] = useState(true)
  // Initialize from pendingWebhook so the deeplink (?create_webhook=1&...)
  // opens the modal on first render with name/description pre-filled.
  const [showCreateModal, setShowCreateModal] = useState(!!pendingWebhook)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<WebhookIntegrationResponse | null>(null)
  const [revealedToken, setRevealedToken] = useState<{ integrationId: string; token: string } | null>(null)

  const loadIntegrations = useCallback(async () => {
    try {
      const data = await webhookService.list()
      setIntegrations(data)
    } catch (error) {
      console.error('Failed to load webhook integrations:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadIntegrations() }, [loadIntegrations])

  const handleCreated = async (created: WebhookIntegrationWithTokenResponse) => {
    setShowCreateModal(false)
    setRevealedToken({ integrationId: created.id, token: created.webhook_token })
    // Opened from the create response, so the one-time token shows even when
    // the list refresh below fails.
    setSelectedIntegration(created)
    try {
      const updated = await webhookService.list()
      setIntegrations(updated)
      setSelectedIntegration(updated.find((i) => i.id === created.id) ?? created)
    } catch (error) {
      console.error('Failed to refresh webhook integrations:', error)
    }
  }

  const handleToggleActive = async (integration: WebhookIntegrationResponse) => {
    try {
      await webhookService.update(integration.id, { is_active: !integration.is_active })
      const updated = await webhookService.list()
      setIntegrations(updated)
      if (selectedIntegration?.id === integration.id) {
        setSelectedIntegration(updated.find((i) => i.id === integration.id) ?? null)
      }
    } catch (error) {
      console.error('Failed to toggle webhook:', error)
    }
  }

  const handleRegenerateToken = async (id: string) => {
    try {
      const updated = await webhookService.regenerateToken(id)
      setRevealedToken({ integrationId: updated.id, token: updated.webhook_token })
      await loadIntegrations()
    } catch (error) {
      console.error('Failed to regenerate token:', error)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await webhookService.delete(id)
      setSelectedIntegration(null)
      if (revealedToken?.integrationId === id) setRevealedToken(null)
      await loadIntegrations()
    } catch (error) {
      console.error('Failed to delete webhook:', error)
    }
  }

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
            <Webhook className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Other Integrations</h3>
            <p className="text-xs text-gray-500">Webhooks & custom integrations</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_CREATE_OPENED); setShowCreateModal(true) }}
            className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-[border-color,box-shadow]"
          >
            <Plus className="w-8 h-8 text-primary-500" />
            <span className="text-xs font-medium text-gray-700">Create Webhook</span>
          </button>
          <button
            onClick={() => { trackEvent(OnboardingEvents.INTEGRATION_REQUEST_OPENED, { category: 'General' }); setShowRequestModal(true) }}
            className="flex flex-col items-center gap-2 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <MessageSquarePlus className="w-8 h-8 text-gray-300" />
            <span className="text-xs font-medium text-gray-400">Request integration</span>
          </button>
        </div>

        {/* Existing webhooks */}
        {loading ? (
          <div className="flex justify-center py-3 mt-3">
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          </div>
        ) : integrations.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {integrations.map((integration) => (
              <button
                key={integration.id}
                onClick={() => { trackEvent(OnboardingEvents.WEBHOOK_OPENED); setSelectedIntegration(integration) }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                  integration.is_active
                    ? 'hover:bg-gray-50'
                    : 'opacity-50 hover:opacity-70'
                }`}
              >
                <Webhook className="w-3.5 h-3.5 text-primary-400 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 truncate">{integration.name}</span>
                {!integration.is_active && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-500 rounded flex-shrink-0">Off</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateWebhookModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(created) => void handleCreated(created)}
          initialName={pendingWebhook?.name}
          initialDescription={pendingWebhook?.description}
        />
      )}
      {showRequestModal && (
        <IntegrationRequestModal category="General" onClose={() => setShowRequestModal(false)} />
      )}
      {selectedIntegration && (
        <WebhookDetailModal
          integration={selectedIntegration}
          revealedToken={revealedToken?.integrationId === selectedIntegration.id ? revealedToken.token : null}
          onClose={() => setSelectedIntegration(null)}
          onToggleActive={() => void handleToggleActive(selectedIntegration)}
          onRegenerateToken={() => void handleRegenerateToken(selectedIntegration.id)}
          onDelete={() => void handleDelete(selectedIntegration.id)}
        />
      )}
    </>
  )
}
